import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Popconfirm, Tooltip } from 'antd';
import type { InputRef } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import './index.less';

/**
 * KvTableEditor —— 「JSON 对象 ⇄ 可编辑表格」通用组件。
 *
 * 系统参数页里有多处配置本质都是字典（评测器白名单 / module_registry paths /
 * bazel 预设），让运维在 JSON 里数逗号既易错又难查。本组件把对象铺成表格：
 * 一行 = 一个 key，列 = 值字段。
 *
 * 几条关键交互约定（改这个文件前先读）：
 *
 * 1. 行 id 必须稳定。每行带一个模块级计数器产出的 id 作为 React key。
 *    绝不能用数组下标 —— 删中间一行后，后面所有行的下标左移，React 会复用错位的
 *    DOM 节点，表现为输入框内容串位 + 正在编辑的格子失焦。
 *
 * 2. 回写用 merge 不用替换。nested 模式下，原对象里 fields 没声明的字段
 *    （以及值是对象/数组的复杂字段）必须原样保留。否则运维在表格里改一个 label，
 *    JSON 里其它字段就被静默删光了 —— 这是本组件最危险的失误。见 buildJson。
 *
 * 3. 外部 value 回流要防「回声」。我们改一格 -> onChange -> 父组件把新 JSON
 *    传回来。这次回流如果照常重建行，id 会全换新，输入框被卸载重建，用户正在打的
 *    字就断在半路。所以用 lastEmittedRef 记住自己最近一次产出，等价就跳过重建。
 *    真正的外部变更（撤销 / 重置 / 从 JSON 视图切回来）与之不等价，照常刷新。
 *
 * 4. 搜索只影响显示。过滤态下隐藏上移/下移 —— 视图里的「上一行」跟数据里的
 *    「上一行」不是同一行，给按钮只会误导人。
 *
 * 5. 校验结果要往上报。表格内部标红只有正在看的人知道；父组件（保存按钮）得靠
 *    onValidityChange 才能在错误状态下禁用保存，而不是等用户点完再弹 message。
 */

/** 一个值字段。shape='flat' 时 fields 只有一项且 name 被忽略 */
export interface KvField {
  /** nested 模式下对象值里的字段名 */
  name: string;
  title: string;
  placeholder?: string;
  /** 用 TextArea 而不是 Input（如 branchDpInfo 多行） */
  multiline?: boolean;
  /** 列宽 flex 权重，默认 1 */
  flex?: number;
  /** 用等宽字体 */
  mono?: boolean;
}

export interface KvTableEditorProps {
  /** 受控值：JSON 对象字符串 */
  value: string;
  onChange: (nextJson: string) => void;
  /** flat: {"k": "v"}；nested: {"k": { ...fields }} */
  shape: 'flat' | 'nested';
  keyTitle: string;
  keyPlaceholder?: string;
  fields: KvField[];
  addText?: string;
  /** 行数 > 该值时显示搜索框，默认 8 */
  searchThreshold?: number;
  emptyHint?: string;
  readOnly?: boolean;
  /** 表格内校验结果变化时回调（null = 合法）。父组件据此 disable 保存按钮 */
  onValidityChange?: (error: string | null) => void;
}

/** flat 模式下值只有一个槽位，借用这个内部名占位（KvField.name 在 flat 下被忽略） */
const FLAT_SLOT = '__value__';

/** 模块级递增计数器：行 id 的唯一来源。不用 Math.random —— 它不稳定且理论上会撞。 */
let rowIdSeq = 0;
const nextRowId = () => (rowIdSeq += 1);

interface Row {
  id: number;
  key: string;
  /** 各字段的文本态（flat 模式挂在 FLAT_SLOT 上） */
  vals: Record<string, string>;
  /** 字段的原始值（仅原始类型）。未被改动时原样回写，避免把 JSON 里的 number/bool 变成字符串 */
  raw: Record<string, unknown>;
  /** fields 没覆盖到的字段 + 值是对象/数组的复杂字段，原样保留（nested 专用） */
  extra: Record<string, unknown>;
  /** 标记哪些字段的原值是对象/数组：这类格子只读，真值躺在 extra 里 */
  complex: Record<string, boolean>;
}

interface ParsedState {
  rows: Row[];
  /** 非 null 表示走降级路径，表格视图不可用 */
  error: string | null;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** 原始类型 -> 展示文本；对象/数组不在这里处理（走 complex 分支） */
const toText = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
};

const PARSE_ERROR = '当前内容不是合法 JSON 对象，无法用表格视图编辑，请切到 JSON 视图修复';

/** 把受控的 JSON 字符串解析成保序行数组。任何无法用表格表达的形态都返回 error 走降级。 */
function parseValue(value: string, shape: 'flat' | 'nested', fields: KvField[]): ParsedState {
  const text = (value ?? '').trim();
  // 空串当空对象：新建配置时父组件往往给 ''
  if (!text) return { rows: [], error: null };

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { rows: [], error: PARSE_ERROR };
  }
  if (!isPlainObject(parsed)) return { rows: [], error: PARSE_ERROR };

  const rows: Row[] = [];
  const fieldNames = new Set(fields.map((f) => f.name));

  for (const [key, rawValue] of Object.entries(parsed)) {
    const vals: Record<string, string> = {};
    const raw: Record<string, unknown> = {};
    const extra: Record<string, unknown> = {};
    const complex: Record<string, boolean> = {};

    if (shape === 'flat') {
      // flat 模式下值必须是标量。值是对象/数组说明 shape 与实际数据不符，同样降级。
      if (isPlainObject(rawValue) || Array.isArray(rawValue)) {
        return { rows: [], error: PARSE_ERROR };
      }
      vals[FLAT_SLOT] = toText(rawValue);
      raw[FLAT_SLOT] = rawValue;
    } else {
      if (!isPlainObject(rawValue)) return { rows: [], error: PARSE_ERROR };
      for (const [k, v] of Object.entries(rawValue)) {
        if (!fieldNames.has(k)) {
          // fields 没声明的字段：原样兜住，回写时 merge 回去
          extra[k] = v;
        } else if (isPlainObject(v) || Array.isArray(v)) {
          // 字段值是复杂结构，文本框表达不了。塞进 extra 原样保留，格子置灰，
          // 免得用户在空框里打字把整个子对象冲掉。
          extra[k] = v;
          complex[k] = true;
          vals[k] = '';
        } else {
          vals[k] = toText(v);
          raw[k] = v;
        }
      }
      for (const f of fields) if (!(f.name in vals)) vals[f.name] = '';
    }

    rows.push({ id: nextRowId(), key, vals, raw, extra, complex });
  }
  return { rows, error: null };
}

/** 行数组 -> JSON 文本。nested 的 merge 语义见文件头约定 2。 */
function buildJson(rows: Row[], shape: 'flat' | 'nested', fields: KvField[]): string {
  const out: Record<string, unknown> = {};
  for (const row of rows) {
    // key 为空的行在对象里无法表达，先跳过（界面上已标红提示用户补全）；
    // key 重复时后写的行覆盖前面的，与 JSON 自身语义一致。
    if (!row.key) continue;

    if (shape === 'flat') {
      const text = row.vals[FLAT_SLOT] ?? '';
      // 没动过就原样回写，保住原来的 number/boolean 类型
      out[row.key] =
        FLAT_SLOT in row.raw && toText(row.raw[FLAT_SLOT]) === text ? row.raw[FLAT_SLOT] : text;
      continue;
    }

    // 先铺 extra（未覆盖字段 + 复杂字段），再叠 fields —— 这一步就是 merge，
    // 少了它用户改一格就会把 JSON 里其它字段静默删掉。
    const obj: Record<string, unknown> = { ...row.extra };
    for (const f of fields) {
      if (row.complex[f.name]) continue; // 真值已在 extra 里，别用空串覆盖
      const text = row.vals[f.name] ?? '';
      if (f.name in row.raw && toText(row.raw[f.name]) === text) {
        obj[f.name] = row.raw[f.name];
      } else if (text !== '' || f.name in row.raw) {
        // text === '' 且该字段原本有值 => 用户是「显式清空」，要写成空串而不是删键。
        // 早先这里只在 text !== '' 时写出，于是清空一格会让该键整个从 JSON 里消失：
        // 运维把 bazel 预设的 baseVersion 清空想重填，切到 JSON 视图发现键没了；
        // 下游校验也从「值不能为空」变成了「缺字段」，报错含义和用户的操作对不上。
        obj[f.name] = text;
      }
      // 原本就没有、现在也留空的字段（例如刚新增的空行）仍然不写出去，免得堆一排空串
    }
    out[row.key] = obj;
  }
  return JSON.stringify(out, null, 2);
}

/** 只比较语义，忽略缩进/空白差异 —— 父组件可能对 JSON 做过格式化再传回来 */
function isJsonEquivalent(a: string, b: string): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(JSON.parse(a)) === JSON.stringify(JSON.parse(b));
  } catch {
    return false;
  }
}

export default function KvTableEditor({
  value,
  onChange,
  shape,
  keyTitle,
  keyPlaceholder,
  fields,
  addText = '新增一行',
  searchThreshold = 8,
  emptyHint = '暂无配置项',
  readOnly = false,
  onValidityChange,
}: KvTableEditorProps): React.JSX.Element {
  // fields 每次渲染都可能是新数组字面量，不能直接进依赖；只有字段名集合变了才需要重解析
  const fieldsSig = useMemo(() => fields.map((f) => f.name).join('\u0000'), [fields]);

  // 「当前 state 是从哪一组输入 parse 出来的」的签名。用它挡住挂载时的重复 parse：
  // 惰性初始化已经 parse 过一遍，下面的 effect 首跑若再 parse 一次，行 id 会全换新，
  // 输入框刚挂载就被卸载重建（每次「表单/JSON」切换都白闪一下）。
  const inputSig = `${shape}\u0000${fieldsSig}\u0000${value}`;

  const [state, setState] = useState<ParsedState>(() => parseValue(value, shape, fields));
  const parsedSigRef = useRef(inputSig);
  const [query, setQuery] = useState('');

  /** 本组件最近一次 onChange 产出的 JSON。用来识别「自己造成的回流」，见文件头约定 3 */
  const lastEmittedRef = useRef<string | null>(null);
  /** 新增行后要聚焦的行 id */
  const pendingFocusRef = useRef<number | null>(null);
  /** 行 id -> key 输入框实例。只在 attach 时写、detach 时删，见下方 ref 回调 */
  const keyInputRefs = useRef<Map<number, InputRef>>(new Map());

  // fields / onChange 存进 ref，让下面的 effect 只在 value / shape / 字段名集合变化时跑，
  // 不被父组件每次渲染新建的数组字面量与内联函数带着空转。
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onValidityChangeRef = useRef(onValidityChange);
  onValidityChangeRef.current = onValidityChange;

  useEffect(() => {
    // 当前 state 就是这组输入 parse 出来的（含挂载首跑），没必要再 parse 一遍
    if (parsedSigRef.current === inputSig) return;
    parsedSigRef.current = inputSig;
    // 回声：父组件把我们刚吐出去的值原封传回。此时 rows 已经是最新的，重建只会
    // 换掉所有行 id -> 输入框卸载重建 -> 光标丢失。直接忽略。
    if (lastEmittedRef.current !== null && isJsonEquivalent(value, lastEmittedRef.current)) return;
    lastEmittedRef.current = null;
    setState(parseValue(value, shape, fieldsRef.current));
    // inputSig 已经包住 value / shape / 字段名集合，它变了才是真正的外部刷新
  }, [inputSig, shape, value]);

  useEffect(() => {
    const id = pendingFocusRef.current;
    if (id === null) return;
    pendingFocusRef.current = null;
    keyInputRefs.current.get(id)?.focus();
  }, [state.rows]);

  /** 唯一的写入口：同步本地行 + 重建 JSON + 上报 */
  const commit = useCallback(
    (nextRows: Row[]) => {
      setState({ rows: nextRows, error: null });
      const json = buildJson(nextRows, shape, fieldsRef.current);
      lastEmittedRef.current = json;
      onChangeRef.current(json);
    },
    [shape],
  );

  const rows = state.rows;

  const patchRow = useCallback(
    (id: number, patch: (row: Row) => Row) => {
      commit(rows.map((r) => (r.id === id ? patch(r) : r)));
    },
    [commit, rows],
  );

  const handleAdd = useCallback(() => {
    const vals: Record<string, string> = {};
    if (shape === 'flat') vals[FLAT_SLOT] = '';
    else for (const f of fields) vals[f.name] = '';
    const row: Row = { id: nextRowId(), key: '', vals, raw: {}, extra: {}, complex: {} };
    pendingFocusRef.current = row.id;
    // 新行 key 为空，必然不匹配当前搜索词。不清掉过滤词用户会以为「点了没反应」。
    setQuery('');
    commit([...rows, row]);
  }, [commit, fields, rows, shape]);

  const handleRemove = useCallback(
    (id: number) => {
      keyInputRefs.current.delete(id);
      commit(rows.filter((r) => r.id !== id));
    },
    [commit, rows],
  );

  const handleMove = useCallback(
    (index: number, delta: number) => {
      const target = index + delta;
      if (target < 0 || target >= rows.length) return;
      const next = rows.slice();
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved);
      commit(next);
    },
    [commit, rows],
  );

  /** key -> 出现次数，用于重复标红 */
  const keyCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      if (!r.key) continue;
      m.set(r.key, (m.get(r.key) ?? 0) + 1);
    }
    return m;
  }, [rows]);

  const duplicatedKeys = useMemo(
    () => [...keyCount.entries()].filter(([, n]) => n > 1).map(([k]) => k),
    [keyCount],
  );
  const emptyKeyCount = useMemo(() => rows.filter((r) => !r.key).length, [rows]);

  /**
   * 实际渲染的列。flat 模式下值只有 FLAT_SLOT 一个槽位，多传的 fields 若照常渲染，
   * 每列都会绑到同一个 slot —— 两列内容互串、React key 还会撞。所以 flat 只取首项，
   * 多传的情况在 dev 下出声（改成运行时截断而不是类型层收窄，是为了不动现有调用点）。
   */
  const renderFields = useMemo(
    () => (shape === 'flat' ? fields.slice(0, 1) : fields),
    [fields, shape],
  );

  useEffect(() => {
    if (import.meta.env.DEV && shape === 'flat' && fields.length > 1) {
      console.warn(
        '[KvTableEditor] shape="flat" 的值只有一个槽位，fields 里第 2 项起会被忽略；' +
          '需要多列请改用 shape="nested"。',
      );
    }
  }, [fields.length, shape]);

  /** 表格自身能判定的校验结果（null = 合法），按值上报给父组件 */
  const validationError = useMemo(() => {
    // 降级态（内容压根不是合法 JSON 对象）本身就是错误：不上报的话，表单视图下
    // 保存按钮会对着一堆无法解析的内容保持可点
    if (state.error) return state.error;
    const problems: string[] = [];
    if (emptyKeyCount > 0) problems.push(`有 ${emptyKeyCount} 行${keyTitle}为空`);
    if (duplicatedKeys.length > 0) problems.push(`${keyTitle}重复：${duplicatedKeys.join('、')}`);
    // 字段值「合法」的口径因配置而异（bazel 预设要求三字段齐全，flat 的值允许留空），
    // KvField 里没有必填声明，这里不擅自判定；交给父组件保存前的 validate* 兜底。
    return problems.length > 0 ? problems.join('；') : null;
  }, [duplicatedKeys, emptyKeyCount, keyTitle, state.error]);

  const lastReportedRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    // 用 ref 按「值」比对上一次上报的结果：结果没变还回调，父组件 setState 会把
    // 双方推进无限 re-render（写法对齐 JsonEditor 的 lastReportedRef）
    if (lastReportedRef.current === validationError) return;
    lastReportedRef.current = validationError;
    onValidityChangeRef.current?.(validationError);
  }, [validationError]);

  const showSearch = rows.length > searchThreshold;
  const filtering = showSearch && query.trim() !== '';

  /** 过滤只影响显示：这里带上原始 index，行操作仍然作用在底层数据上 */
  const visibleRows = useMemo(() => {
    const all = rows.map((row, index) => ({ row, index }));
    if (!filtering) return all;
    const q = query.trim().toLowerCase();
    return all.filter(({ row }) => {
      if (row.key.toLowerCase().includes(q)) return true;
      return Object.values(row.vals).some((v) => v.toLowerCase().includes(q));
    });
  }, [filtering, query, rows]);

  /** grid 列：序号 + key 列 + 各字段（按 flex 权重）+ 操作列 */
  const colTemplate = useMemo(() => {
    const parts = ['40px', 'minmax(150px, 1.2fr)'];
    for (const f of renderFields) parts.push(`minmax(140px, ${f.flex ?? 1}fr)`);
    if (!readOnly) parts.push('92px');
    return parts.join(' ');
  }, [renderFields, readOnly]);

  /** 窄屏时整体横向滚动，不让输入框被挤成一条缝 */
  const minWidth = useMemo(() => {
    const base = 40 + 150 + (readOnly ? 0 : 92) + 24;
    return base + renderFields.reduce((sum, f) => sum + (f.multiline ? 200 : 140), 0);
  }, [renderFields, readOnly]);

  const gridStyle = { gridTemplateColumns: colTemplate, minWidth } as React.CSSProperties;

  // ---- 降级：内容不是合法 JSON 对象 / 与 shape 不匹配 ----
  // 注意这里不动 value、也不吐 onChange：用户的原始文本必须原样留在 JSON 视图里等他修。
  if (state.error) {
    return (
      <div className="kv-table-editor">
        <div className="kv-table-fallback">
          <div className="kv-table-fallback-title">表格视图不可用</div>
          <div className="kv-table-fallback-desc">{state.error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="kv-table-editor">
      <div className="kv-table-toolbar">
        <span className="kv-table-count">
          共 <b>{rows.length}</b> 项
          {filtering ? (
            <span className="kv-table-count-sub">（筛选出 {visibleRows.length} 项）</span>
          ) : null}
        </span>
        <div className="kv-table-toolbar-right">
          {showSearch ? (
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined className="kv-table-search-icon" />}
              placeholder="搜索 key 或内容"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="kv-table-search"
            />
          ) : null}
          {readOnly ? null : (
            <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={handleAdd}>
              {addText}
            </Button>
          )}
        </div>
      </div>

      {duplicatedKeys.length > 0 || emptyKeyCount > 0 ? (
        <div className="kv-table-warn">
          {duplicatedKeys.length > 0 ? (
            <div>
              key 重复：{duplicatedKeys.join('、')}
              <span className="kv-table-warn-sub">（保存时后一行会覆盖前一行）</span>
            </div>
          ) : null}
          {emptyKeyCount > 0 ? (
            <div>
              有 {emptyKeyCount} 行 key 为空
              <span className="kv-table-warn-sub">（空 key 不会写入 JSON）</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div className="kv-table-empty">
          <div className="kv-table-empty-hint">{emptyHint}</div>
          {readOnly ? null : (
            <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={handleAdd}>
              {addText}
            </Button>
          )}
        </div>
      ) : (
        <div className="kv-table-scroll">
          <div className="kv-table-grid">
            <div className="kv-table-head" style={gridStyle}>
              <div className="kv-table-cell kv-table-cell--idx">#</div>
              <div className="kv-table-cell">{keyTitle}</div>
              {/* 列的顺序完全由 fields prop 决定，行内不存在插删重排，下标做 key 是安全的；
                  反倒是 flat 下 slot 恒为常量 FLAT_SLOT，拿它当 key 会重复 */}
              {renderFields.map((f, i) => (
                <div className="kv-table-cell" key={i}>
                  {f.title}
                </div>
              ))}
              {readOnly ? null : <div className="kv-table-cell kv-table-cell--ops">操作</div>}
            </div>

            {visibleRows.map(({ row, index }) => {
              const dup = row.key !== '' && (keyCount.get(row.key) ?? 0) > 1;
              const keyInvalid = row.key === '' || dup;
              return (
                // key 必须是 row.id：用 index 会让删除中间行后的输入框串位/失焦
                <div className="kv-table-row" key={row.id} style={gridStyle}>
                  <div className="kv-table-cell kv-table-cell--idx">{index + 1}</div>

                  <div className="kv-table-cell">
                    <Input
                      size="small"
                      ref={(el) => {
                        // 卸载时 React 会带 null 回调一次：这时删掉条目。只写不删的话，
                        // 外部 value 刷新（行全部重建）后旧 id 会永久残留，Map 越用越长
                        if (el) keyInputRefs.current.set(row.id, el);
                        else keyInputRefs.current.delete(row.id);
                      }}
                      status={keyInvalid ? 'error' : undefined}
                      disabled={readOnly}
                      placeholder={keyPlaceholder}
                      value={row.key}
                      onChange={(e) => {
                        const key = e.target.value;
                        patchRow(row.id, (r) => ({ ...r, key }));
                      }}
                      className="kv-table-input kv-table-input--mono"
                    />
                  </div>

                  {renderFields.map((f, i) => {
                    const slot = shape === 'flat' ? FLAT_SLOT : f.name;
                    const locked = readOnly || !!row.complex[slot];
                    const text = row.vals[slot] ?? '';
                    const onText = (next: string) =>
                      patchRow(row.id, (r) => ({ ...r, vals: { ...r.vals, [slot]: next } }));
                    const cls = `kv-table-input${f.mono ? ' kv-table-input--mono' : ''}`;
                    const ph = row.complex[slot] ? '复杂值，请在 JSON 视图编辑' : f.placeholder;
                    return (
                      <div className="kv-table-cell" key={i}>
                        {f.multiline ? (
                          <Input.TextArea
                            size="small"
                            autoSize={{ minRows: 1, maxRows: 6 }}
                            disabled={locked}
                            placeholder={ph}
                            value={text}
                            onChange={(e) => onText(e.target.value)}
                            className={cls}
                          />
                        ) : (
                          <Input
                            size="small"
                            disabled={locked}
                            placeholder={ph}
                            value={text}
                            onChange={(e) => onText(e.target.value)}
                            className={cls}
                          />
                        )}
                      </div>
                    );
                  })}

                  {readOnly ? null : (
                    <div className="kv-table-cell kv-table-cell--ops">
                      {/* 过滤态下「上一行」在视图与数据里不是同一行，给按钮只会误导人 */}
                      {filtering ? null : (
                        <>
                          <Tooltip title="上移">
                            <Button
                              type="text"
                              size="small"
                              icon={<ArrowUpOutlined />}
                              disabled={index === 0}
                              onClick={() => handleMove(index, -1)}
                            />
                          </Tooltip>
                          <Tooltip title="下移">
                            <Button
                              type="text"
                              size="small"
                              icon={<ArrowDownOutlined />}
                              disabled={index === rows.length - 1}
                              onClick={() => handleMove(index, 1)}
                            />
                          </Tooltip>
                        </>
                      )}
                      <Popconfirm
                        title="删除该行？"
                        description={row.key ? `key：${row.key}` : undefined}
                        okText="删除"
                        okButtonProps={{ danger: true }}
                        cancelText="取消"
                        onConfirm={() => handleRemove(row.id)}
                      >
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </div>
                  )}
                </div>
              );
            })}

            {filtering && visibleRows.length === 0 ? (
              <div className="kv-table-no-match">没有匹配「{query.trim()}」的行</div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
